import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Input, message, Divider, Typography } from 'antd';
import { StylesProps, RouterProps } from 'src/library/types';
import { MOBILE_BREAK_POINT } from 'src/globals';
import GenericHoc from 'src/common/generic-hoc';
import { HocOptions } from 'src/common/generic-hoc.types';
import ShipsyLogoUrl from 'src/assets/logos/shipsyLogo.png';
import { vehicleRequestsRoute, loginViaOtpRoute, forgotPasswordRoute } from 'src/routing/routeConstants';
import { loadInitialData } from 'src/common/actions/loadInitialData';
import FloatLabel from 'src/common/FloatLabel';
import { verifyLogin, getOrgDetails } from 'src/api/auth';
import SubmitOtp from './SubmitOtp';
import Loader from 'src/common/Loader';
import config from 'src/config';

const FormItem = Form.Item;
const { Text } = Typography;

const styles = (theme: any) => ({
  mainDiv: {
    minHeight: '100vh',
    backgroundColor: theme.bodyBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  header: {
    marginTop: '20px',
    color: '#2F2D3B',
    fontSize: 18,
  },
  cardDiv: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: {
      left: '20px',
      right: '20px',
      bottom: '20px',
    },
  },
  cardStyle: {
    width: '600px',
    border: 'none',
  },
  inputDiv: {
    minWidth: '343px',
    marginTop: 20,
  },
  submitButton: {
    height: '40px',
    marginTop: '10px',
    width: '100%',
    maxWidth: '343px',
  },
  link: {
    color: '#165492',
    fontWeight: 600,
  },
  [`@media (max-width: ${MOBILE_BREAK_POINT}px)`]: {
    mainDiv: {
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    header: {
      fontSize: 16,
    },
    cardStyle: {
      width: '100%',
      border: 'none',
    },
    inputDiv: {
      width: '100%',
    },
  },
  formItem: {
    marginBottom: '10px',
    width: '100%',
  },
  password: {
    width: '100%',
    '&:hover': {
      opacity: 0.5,
      transition: 'none',
    },
  },
  forgotPassword: {
    textAlign: 'right',
    cursor: 'default',
  },
});

interface Props extends StylesProps<ReturnType<typeof styles>>, RouterProps {
  dispatch: (fn: (dispatch: any, getState: any) => Promise<void>) => void;
  t;
}

const EmailLogin: React.FC<Props> = (props: Props) => {
  const { classes, history, dispatch, t } = props;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [logoUrl, setLogoUrl] = useState<string>(ShipsyLogoUrl);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [showOtp, setShowOtp] = useState<boolean>(false);
  const [otpData, setOtpData] = useState<any>({});
  const [organisationDetails, setOrganisationDetails] = useState<any>({});

  const hideLoginViaOTP = organisationDetails?.hide_login_via_otp || false;

  const handleSubmit = async (otp?: string) => {
    if (!otp) {
      await form.validateFields();
    }
    setLoading(true);
    const reqBody: any = {
      email: form.getFieldValue('email'),
      password: form.getFieldValue('password'),
    };
    if (otp) {
      reqBody.otp = otp;
    }
    const response = await verifyLogin(reqBody);
    if (response.isSuccess) {
      if (response.data?.otp_sent && !otp) {
        const otpUnblockDuration = response.data?.otp_unblock_duration || 30;
        const otpUnblockTime = response.data?.otp_unblock_time;
        setOtpData({
          username: form.getFieldValue('email'),
          phone: response.data?.phone,
          otpUnblockDuration,
          otpUnblockTime,
          organisationDetails,
        });
        setShowOtp(true);
        setLoading(false);
        return;
      }
      await dispatch(loadInitialData());
      history.replace(vehicleRequestsRoute);
    } else {
      message.error(response.errorMessage);
    }
    setLoading(false);
  };

  const loadOrgDetails = async () => {
    setImageLoading(true);
    const orgDetails = await getOrgDetails();
    setOrganisationDetails(orgDetails?.data);
    setLogoUrl(orgDetails?.data?.logoUrl || ShipsyLogoUrl);
    window.localStorage.setItem('organisationID', orgDetails?.data?.organisation_id || config.DEFAULT_ORG_PRETTY_NAME);
    window.localStorage.setItem('logoUrl', orgDetails?.data?.logoUrl || ShipsyLogoUrl);
    setImageLoading(false);
  };
  useEffect(() => {
    loadOrgDetails();
  }, []);

  if (imageLoading) {
    return <Loader />;
  }

  if (showOtp) {
    return (
      <SubmitOtp
        username={otpData.username}
        phone={otpData.phone}
        otpUnblockDuration={otpData.otpUnblockDuration}
        otpUnblockTime={otpData.otpUnblockTime}
        organisationDetails={otpData.organisationDetails}
        onBack={() => setShowOtp(false)}
        onSubmitOtp={(e, otp) => handleSubmit(otp)}
      />
    );
  }

  return (
    <div className={classes.mainDiv}>
      <div className={classes.contentDiv}>
        <Card className={classes.cardStyle} bordered={false}>
          <Form form={form} onFinish={() => handleSubmit()} className="login-form" layout="vertical">
            <div className={classes.cardDiv}>
              <img
                style={{
                  height: 'auto',
                  maxWidth: '246px',
                  maxHeight: '100px',
                  objectFit: 'contain',
                }}
                src={logoUrl}
                alt="Shipsy"
              />
              <p className={classes.header}>{t('Welcome to Vendor Portal')}</p>
              <div className={classes.inputDiv}>
                <FormItem
                  name='email'
                  rules={[
                    {
                        type: 'email',
                        message: t('Please enter valid email id'),
                    },
                    {
                        required: true,
                        message: t('Email must be present!'),
                    },
                  ]}
                  className={classes.formItem}
                >
                  <FloatLabel
                    label={t('Email')}
                    value={form.getFieldValue('email')}
                  >
                    <Input
                      size="large"
                      style={{
                        width: '100%',
                      }}
                      onChange={(event) => form.setFieldsValue({ email: event?.target?.value })}
                    />
                  </FloatLabel>
                </FormItem>
                <FormItem
                  name='password'
                  rules={[
                    {
                        required: true,
                        message: t('Password must be present!'),
                    },
                  ]}
                  className={classes.formItem}
                >
                  <FloatLabel
                    label={t('Password')}
                    value={form.getFieldValue('password')}
                  >
                    <Input.Password
                      className={classes.password}
                      size="large"
                      onChange={(event) => form.setFieldsValue({ password: event?.target?.value })}
                    />
                  </FloatLabel>
                </FormItem>
                <div
                  className={classes.forgotPassword}
                >
                  <Text
                    onClick={() => history.push(forgotPasswordRoute)}
                  >
                    {t('Forgot Password?')}
                  </Text>
                </div>
              </div>
              <Button
                type="primary"
                className={classes.submitButton}
                htmlType="submit"
                loading={loading}
              >
                {t('LOG IN')}
              </Button>
              {!hideLoginViaOTP && (
                <>
                  <Divider>{t('OR')}</Divider>
                  <Button
                    className={classes.submitButton}
                    onClick={() => history.push(loginViaOtpRoute)}
                  >
                    {t('LOGIN VIA OTP')}
                  </Button>
                </>
              )}
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
  },
  connectJss: {
    useJss: true,
    styleSheet: styles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(EmailLogin);
